import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { validateFolderName, getCharacterCountDisplay } from '../utils/nameValidator';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Folders() {
  const { projectKey, folderType } = useParams();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [error, setError] = useState('');
  const [selectedFolders, setSelectedFolders] = useState([]);

  useEffect(() => {
    if (projectKey) {
      fetchProject();
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectId && folderType) {
      fetchFolders();
    }
  }, [projectId, folderType]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
          setProjectId(projectData.id || projectData._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/folders?projectId=${projectId}&type=${folderType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFolders(data.data || []);
      }
    } catch (err) {
      console.error('Error loading folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate folder name with normalization
    const validation = validateFolderName(formData.name, { normalize: true });
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }
    
    const trimmedName = validation.normalized;

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          description: formData.description,
          projectId,
          type: folderType,
          parentId: selectedParent,
        }),
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ name: '', description: '' });
        setSelectedParent(null);
        setError('');
        fetchFolders();
        // Dispatch event to refresh sidebar
        const eventType = folderType === 'requirement' ? 'requirementFolders' : 'testcaseFolders';
        window.dispatchEvent(new CustomEvent('refreshSidebar', { detail: { type: eventType } }));
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create folder');
        if (data.details) {
          setError(`${data.error || 'Failed to create folder'}: ${data.details}`);
        }
      }
    } catch (err) {
      setError('Error creating folder: ' + err.message);
      console.error(err);
    }
  };

  const handleNameChange = (name) => {
    setFormData({ ...formData, name });
    
    // Real-time validation
    if (name.trim().length > 0) {
      const validation = validateFolderName(name, { normalize: false });
      setValidationErrors(validation.errors);
      setValidationWarnings(validation.warnings);
    } else {
      setValidationErrors([]);
      setValidationWarnings([]);
    }
  };

  const handleDelete = async folderId => {
    if (!window.confirm('Are you sure you want to delete this folder? It must be empty.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/archive/folders/${folderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setError('');
        fetchFolders();
        // Dispatch event to refresh sidebar
        const eventType = folderType === 'requirement' ? 'requirementFolders' : 'testcaseFolders';
        window.dispatchEvent(new CustomEvent('refreshSidebar', { detail: { type: eventType } }));
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete folder');
      }
    } catch (err) {
      alert('Error deleting folder');
      // Error logged for debugging
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFolders.length === 0) {
      setError('No folders selected');
      return;
    }
    if (!window.confirm(`Delete ${selectedFolders.length} folder(s)? This will only delete empty folders.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failedCount = 0;
      const errors = [];
      
      for (const folderId of selectedFolders) {
        try {
          const response = await fetch(`${API_URL}/folders/${folderId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            successCount++;
          } else {
            failedCount++;
            const data = await response.json();
            errors.push(data.error || 'Unknown error');
          }
        } catch (err) {
          failedCount++;
          errors.push(err.message);
        }
      }
      
      setSelectedFolders([]);
      fetchFolders();
      
      // Dispatch event to refresh sidebar
      const eventType = folderType === 'requirement' ? 'requirementFolders' : 'testcaseFolders';
      window.dispatchEvent(new CustomEvent('refreshSidebar', { detail: { type: eventType } }));
      
      if (failedCount > 0) {
        setError(`Deleted ${successCount} folder(s), ${failedCount} failed (${errors[0]})`);
      } else {
        setError('');
      }
      
      setTimeout(() => setError(''), 5000);
    } catch (err) {
      setError('Failed to delete folders');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedFolders.length === 0) {
      setError('No folders selected');
      return;
    }
    if (!window.confirm(`Archive ${selectedFolders.length} folder(s)? Folders and their contents will be moved to archived items.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failedCount = 0;
      
      for (const folderId of selectedFolders) {
        try {
          const response = await fetch(`${API_URL}/archive/folders/${folderId}`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify({ cascadeContents: true }),
          });
          if (response.ok) {
            successCount++;
          } else {
            failedCount++;
          }
        } catch (err) {
          failedCount++;
        }
      }
      
      setSelectedFolders([]);
      fetchFolders();
      
      if (failedCount > 0) {
        setError(`Archived ${successCount} folder(s), ${failedCount} failed`);
      } else {
        setError('');
      }
      
      setTimeout(() => setError(''), 5000);
    } catch (err) {
      setError('Failed to archive folders');
    }
  };

  const handleArchive = async (folderId) => {
    if (!window.confirm('Archive this folder and all its contents?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/archive/folders/${folderId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ cascadeContents: true }),
      });
      if (response.ok) {
        setError('');
        fetchFolders();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to archive folder');
      }
    } catch (err) {
      alert('Error archiving folder');
    }
  };

  const handleSelectFolder = (folderId) => {
    setSelectedFolders(prev =>
      prev.includes(folderId) ? prev.filter(id => id !== folderId) : [...prev, folderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFolders.length === folders.length) {
      setSelectedFolders([]);
    } else {
      setSelectedFolders(folders.map(f => f.id || f._id));
    }
  };

  const renderFolderTree = (parentId = null, level = 0) => {
    const childFolders = folders.filter(f => {
      const fParentId = f.parentId?.id || f.parentId?._id || f.parentId;
      return parentId === null ? !fParentId : fParentId === parentId;
    });

    return childFolders.map(folder => {
      const folderId = folder.id || folder._id;
      const isSelected = selectedFolders.includes(folderId);
      return (
        <div key={folderId} style={{ marginLeft: `${level * 20}px` }} className="mb-2">
          <div className={`card ${isSelected ? 'border-primary' : ''}`}>
            <div className="card-body py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    checked={isSelected}
                    onChange={() => handleSelectFolder(folderId)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="me-2">📁</span>
                  <strong>{folder.name}</strong>
                  {folder.description && <small className="text-muted ms-2">{folder.description}</small>}
                </div>
                <div className="btn-group btn-group-sm">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setSelectedParent(folderId);
                      setShowModal(true);
                    }}
                  >
                    + Subfolder
                  </button>
                  <button 
                    className="btn btn-outline-warning" 
                    onClick={() => handleArchive(folderId)}
                    title="Archive folder and contents"
                  >
                    🗄️ Archive
                  </button>
                  <button className="btn btn-outline-danger" onClick={() => handleDelete(folderId)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          {renderFolderTree(folderId, level + 1)}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow-1">
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/projects">Projects</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectId}`}>{project?.name}</Link>
            </li>
            <li className="breadcrumb-item active">
              {folderType === 'requirement' ? 'Requirement' : 'Test Case'} Folders
            </li>
          </ol>
        </nav>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {folderType === 'requirement' ? 'Requirement' : 'Test Case'} Folders
          {selectedFolders.length > 0 && (
            <small className="text-muted ms-3">({selectedFolders.length} selected)</small>
          )}
        </h2>
        <div className="btn-group">
          {folders.length > 0 && (
            <button
              className="btn btn-outline-secondary"
              onClick={handleSelectAll}
              title={selectedFolders.length === folders.length ? 'Deselect all' : 'Select all'}
            >
              {selectedFolders.length === folders.length ? '☐ Deselect All' : '☑ Select All'}
            </button>
          )}
          {selectedFolders.length > 0 && (
            <>
              <button
                className="btn btn-warning"
                onClick={handleBulkArchive}
                title="Archive selected folders and their contents"
              >
                🗄️ Archive ({selectedFolders.length})
              </button>
              <button
                className="btn btn-danger"
                onClick={handleBulkDelete}
                title="Delete selected folders"
              >
                🗑️ Delete ({selectedFolders.length})
              </button>
            </>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              setSelectedParent(null);
              setShowModal(true);
            }}
          >
            + Create Folder
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      <div className="mb-4">{renderFolderTree()}</div>

      {folders.length === 0 && (
        <div className="alert alert-info">
          No folders created yet. Create a folder to organize your{' '}
          {folderType === 'requirement' ? 'requirements' : 'test cases'}.
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Create {selectedParent ? 'Subfolder' : 'Folder'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {selectedParent && (
                    <div className="alert alert-info">
                      Creating subfolder under:{' '}
                      <strong>{folders.find(f => (f.id || f._id) === selectedParent)?.name}</strong>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Folder Name *</label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.length > 0 ? 'is-invalid' : ''}`}
                      maxLength="200"
                      placeholder="e.g., User Authentication / Login & Security"
                      value={formData.name}
                      onChange={e => handleNameChange(e.target.value)}
                      required
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Max 200 characters. Letters, numbers, spaces, and common punctuation (- / &amp; . , etc.) are allowed.
                    </small>
                    {formData.name.length > 0 && (
                      <small className={getCharacterCountDisplay(formData.name.length, 200).className}>
                        {getCharacterCountDisplay(formData.name.length, 200).text}
                      </small>
                    )}
                    {validationWarnings.length > 0 && (
                      <div className="alert alert-warning mt-2 py-2 px-3" style={{ fontSize: '0.875rem' }}>
                        {validationWarnings.map((warning, idx) => (
                          <div key={idx}>• {warning}</div>
                        ))}
                      </div>
                    )}
                    {validationErrors.length > 0 && (
                      <div className="invalid-feedback d-block">
                        {validationErrors.map((error, idx) => (
                          <div key={idx}>• {error}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowModal(false);
                      setSelectedParent(null);
                      setValidationErrors([]);
                      setValidationWarnings([]);
                      setFormData({ name: '', description: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Folders;
